import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import { FiCheck, FiX } from 'react-icons/fi';

interface PoolParamsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  initialPoolSize?: number; // Kilolitres
  initialElectricityCost?: number; // Cents/KWh
  onSave?: (poolSize: number, electricityCost: number) => void | Promise<void>;
}

const PoolParamsSidebar: React.FC<PoolParamsSidebarProps> = ({
  isOpen,
  onClose,
  initialPoolSize,
  initialElectricityCost,
  onSave,
}) => {
  const [poolSizeDraft, setPoolSizeDraft] = useState<string>(
    initialPoolSize !== undefined && initialPoolSize !== null ? String(initialPoolSize) : ''
  );
  const [costDraft, setCostDraft] = useState<string>(
    initialElectricityCost !== undefined && initialElectricityCost !== null ? String(initialElectricityCost) : ''
  );
  const [awaitConfirm, setAwaitConfirm] = useState(false);

  useEffect(() => {
    setPoolSizeDraft(initialPoolSize !== undefined && initialPoolSize !== null ? String(initialPoolSize) : '');
  }, [initialPoolSize]);

  useEffect(() => {
    setCostDraft(initialElectricityCost !== undefined && initialElectricityCost !== null ? String(initialElectricityCost) : '');
  }, [initialElectricityCost]);

  const parsedValues = useMemo(() => {
    const poolSize = parseInt(poolSizeDraft, 10);
    const cost = parseFloat(costDraft);
    return {
      poolSize: Number.isFinite(poolSize) ? poolSize : NaN,
      cost: Number.isFinite(cost) ? cost : NaN,
    };
  }, [poolSizeDraft, costDraft]);

  const isValid = useMemo(() => {
    return (
      Number.isFinite(parsedValues.poolSize) && parsedValues.poolSize > 0 &&
      Number.isFinite(parsedValues.cost) && parsedValues.cost >= 0
    );
  }, [parsedValues]);

  const hasChanges = useMemo(() => {
    const initialSize = initialPoolSize ?? NaN;
    const initialCost = initialElectricityCost ?? NaN;
    return (
      Number.isFinite(parsedValues.poolSize) && parsedValues.poolSize !== initialSize ||
      Number.isFinite(parsedValues.cost) && parsedValues.cost !== initialCost
    );
  }, [parsedValues, initialPoolSize, initialElectricityCost]);

  const handleRequestSave = () => {
    if (!isValid || !hasChanges) return;
    setAwaitConfirm(true);
  };

  const handleConfirm = async () => {
    if (!onSave || !isValid) {
      setAwaitConfirm(false);
      return;
    }
    await onSave(parsedValues.poolSize, parsedValues.cost);
    setAwaitConfirm(false);
  };

  const handleCancelConfirm = () => {
    setAwaitConfirm(false);
  };

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Pool Parameters">
      <div className="space-y-6">
        {/* Editable card */}
        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Basics</h4>

          {/* Pool Size (Kilolitres) */}
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Pool Size</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                step={1}
                className="px-2 py-1 border border-gray-300 dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 w-full"
                value={poolSizeDraft}
                onChange={(e) => setPoolSizeDraft(e.target.value)}
                placeholder="Enter size"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Kilolitres</span>
            </div>
            {!Number.isFinite(parsedValues.poolSize) || parsedValues.poolSize <= 0 ? (
              <div className="mt-1 text-xs text-red-500 dark:text-red-400">Please enter a valid positive integer.</div>
            ) : null}
          </div>

          {/* Electricity Cost (Cents/KWh) */}
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Electricity Cost</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                step={0.1}
                className="px-2 py-1 border border-gray-300 dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 w-full"
                value={costDraft}
                onChange={(e) => setCostDraft(e.target.value)}
                placeholder="Enter cost"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Cents/KWh</span>
            </div>
            {!Number.isFinite(parsedValues.cost) || parsedValues.cost < 0 ? (
              <div className="mt-1 text-xs text-red-500 dark:text-red-400">Please enter a valid non-negative number.</div>
            ) : null}
          </div>

          {/* Save button */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              onClick={handleRequestSave}
              disabled={!isValid || !hasChanges}
              aria-label="Save Pool Parameters"
            >
              Save
            </button>
          </div>

          {/* Confirm bar */}
          {awaitConfirm && (
            <div className="mt-3 p-3 rounded-md border border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20">
              <div className="text-sm text-gray-700 dark:text-gray-200 mb-2">
                Confirm update to Pool Size: <span className="font-semibold">{parsedValues.poolSize}</span> Kilolitres,
                Electricity Cost: <span className="font-semibold">{parsedValues.cost}</span> Cents/KWh?
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center text-green-600 dark:text-green-400 hover:text-green-700"
                  onClick={handleConfirm}
                  aria-label="Confirm"
                >
                  <FiCheck className="h-4 w-4 mr-1" /> Confirm
                </button>
                <button
                  type="button"
                  className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-700"
                  onClick={handleCancelConfirm}
                  aria-label="Cancel"
                >
                  <FiX className="h-4 w-4 mr-1" /> Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default PoolParamsSidebar;